import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Grid, Typography } from '@mui/material';
import moment from 'moment';

import { FHCopyToClipboard, FHPopper, FHSkeletonLoader } from '../../base';
import { FORTA_BLUE, FORTA_GRAY4, FORTA_GRAY3, FORTA_GRAY1 } from '../../../utils/colors';
import { capitalizeFirstLetter, phoneNumberFormatter } from '../../../helpers';
import ErrorAlertIcon from '../../svgs/ErrorAlertIcon';
import ContactCardIcon from '../../../assets/images/contact-card.svg';
import { ClinicalRelationships } from '../../../constants';
import {
  getBCBAProvierLabels,
  getBTLabels,
  getDiagnosisLabels,
  getLanguageLabels,
  getParentsLabels,
  getSiblingLabels,
  NO_ACTIVE_TP_FOUND,
  NO_BT_ASSIGNED,
  NOT_FOUND,
} from './utils';

import type { RootState } from '../../../store';
import type { IClient, IOption, IProviderAssignment, ITreatmentPlan } from '../../../utils/types';

interface IClientInsightDetailsProps {
  tpInfoLoading: boolean;
  cardStyles?: any;
  containerStyles?: any;
  filterAssignmentsByClientId?: boolean;
  selectedClient?: IOption | null;
  treatmentPlanInfo?: ITreatmentPlan | null;
}

interface IClientInsightCard {
  loading: boolean;
  sx?: any;
  title: string;
  labels: string[];
  showPoperIcon?: boolean;
  handleClick?: (id: number) => void;
}

interface IListItem {
  id: number;
  label: string;
  showPoperIcon?: boolean;
  handleClick?: (id: number) => void;
}

function ListItem({ id, label, showPoperIcon, handleClick }: IListItem) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
      <Box
        sx={{
          backgroundColor: FORTA_GRAY3,
          minWidth: '10px',
          minHeight: '10px',
          borderRadius: '50%',
          mr: 1,
        }}
      ></Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ wordWrap: 'break-word' }} display={'inline'} mr={1}>
          {label}
        </Typography>
        {showPoperIcon &&
          label !== NOT_FOUND &&
          label !== NO_ACTIVE_TP_FOUND &&
          label !== NO_BT_ASSIGNED && (
            <img
              src={ContactCardIcon}
              alt="Contact Card"
              width={21}
              height={21}
              style={{ cursor: 'pointer' }}
              onClick={() => handleClick && handleClick(id)}
            />
          )}
      </Box>
    </Box>
  );
}

function ClientInsightCard({
  sx = {},
  title,
  labels = [],
  loading,
  showPoperIcon,
  handleClick,
}: IClientInsightCard) {
  return (
    <Box
      sx={{
        border: `1px solid ${FORTA_GRAY3}`,
        p: 2,
        borderRadius: '8px',
        boxShadow: '0 4px 4px rgba(0, 0, 0, 0.1)',
        ...sx,
      }}
    >
      <Typography fontWeight={700} color={FORTA_GRAY4}>
        {title}
      </Typography>
      {loading ? (
        <FHSkeletonLoader containerStyles={{ mt: 1 }} />
      ) : (
        labels.map((label, i) => (
          <ListItem
            label={label}
            id={i}
            key={i}
            showPoperIcon={showPoperIcon}
            handleClick={handleClick}
          />
        ))
      )}
    </Box>
  );
}

function ClientInsightDetails({
  selectedClient = null,
  tpInfoLoading,
  filterAssignmentsByClientId = false,
  treatmentPlanInfo,
  cardStyles = {},
  containerStyles = {},
}: IClientInsightDetailsProps) {
  const {
    selected: client,
    loading: clientLoading,
    clientsFamily,
    clientFamilyLoading,
  } = useSelector((state: RootState) => state.clients);
  const { list: assignments = [], loading: providerAssignmentsLoading } = useSelector(
    (state: RootState) => state.providerAssignment,
  );

  const providerAssignments = filterAssignmentsByClientId
    ? assignments.filter((a) => a.client.id === selectedClient?.id)
    : assignments;

  const activeTPId = client?.active_treatment_plan_id;

  const parentCardRef = useRef();
  const btCardRef = useRef();
  const homeBCBARef = useRef();
  const bobingBCBARef = useRef();
  const substitudeBCBARef = useRef();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [poperContent, setPoperContent] = useState<{ [key: string]: string | number }>({});
  const [openPoper, setOpenPoper] = useState<boolean>(false);

  const handleClose = () => {
    setAnchorEl(null);
    setOpenPoper(false);
    setPoperContent({});
  };

  const handleParentListItemClick = (index: number, _treatmentPlanInfo: ITreatmentPlan | null) => {
    if (!clientsFamily.length) return;
    const resource = clientsFamily[0].parents_and_caregivers[index];
    if (!resource) return;
    const data = {
      type: 'Parent Info',
      id: resource.id ?? 'N/A',
      name: resource.name,
      email: resource.email ?? 'N/A',
      phone: resource.phone ?? 'N/A',
    };
    setPoperContent(data);
    setOpenPoper(true);
  };

  const handleBCBAListItemClick = (
    index: number,
    providerAssignments: IProviderAssignment[],
    type: ClinicalRelationships,
  ) => {
    if (!providerAssignments?.length) return;
    const filteredProviders = providerAssignments.filter((p) => p.clinical_relationship === type);
    if (index > filteredProviders.length - 1) return;
    const resource = filteredProviders[index];
    if (!resource) return;
    const data = {
      type: 'BCBA Info',
      id: resource?.id ?? 'N/A',
      name: `${resource?.provider?.first_name} ${resource?.provider?.last_name}`,
      email: resource?.provider?.email ?? 'N/A',
      phone: resource?.provider?.phone ?? 'N/A',
    };
    setPoperContent(data);
    setOpenPoper(true);
  };

  const handleBTListItemClick = (index: number, client: IClient | null) => {
    if (!client?.providers?.length) return;
    const resource = client.providers[index];
    if (!resource) return;
    const data = {
      type: 'BT Info',
      id: resource.id ?? 'N/A',
      name: `${resource.first_name} ${resource.last_name}`,
      email: resource.email ?? 'N/A',
      phone: resource.phone ?? 'N/A',
    };
    setPoperContent(data);
    setOpenPoper(true);
  };

  return (
    <>
      {selectedClient ? (
        <Box sx={{ px: 2, ...containerStyles }} data-testid="clientInsightDetailsContainer">
          <Grid container mt={0.2} fontSize={16} spacing={2} textAlign="left">
            <Grid item xs={12} sm={12} md={6}>
              <ClientInsightCard
                sx={{ ...cardStyles }}
                loading={clientLoading}
                title="Date of birth"
                labels={[moment(client?.dob).format('MM/DD/YYYY')]}
              />
              <ClientInsightCard
                loading={clientLoading}
                sx={{ mt: 2, ...cardStyles }}
                title="City and state"
                labels={[
                  `${
                    client?.street_address_city
                      ? capitalizeFirstLetter(client?.street_address_city)
                      : ''
                  },
                ${client?.street_address_state?.toUpperCase()}`,
                ]}
              />
              <ClientInsightCard
                sx={{ mt: 2, ...cardStyles }}
                title="Diagnosis"
                loading={tpInfoLoading || clientLoading}
                labels={getDiagnosisLabels(treatmentPlanInfo ?? null, activeTPId)}
              />
              <ClientInsightCard
                sx={{ mt: 2, ...cardStyles }}
                title="Siblings"
                loading={tpInfoLoading || clientLoading}
                labels={getSiblingLabels(treatmentPlanInfo ?? null, activeTPId)}
              />
              <ClientInsightCard
                sx={{ mt: 2, ...cardStyles }}
                title="Language"
                loading={tpInfoLoading || clientLoading}
                labels={getLanguageLabels(treatmentPlanInfo ?? null, activeTPId)}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <ClientInsightCard
                title="Parents"
                loading={clientFamilyLoading}
                sx={{ ...cardStyles }}
                labels={getParentsLabels(clientsFamily ?? [])}
                showPoperIcon={!openPoper}
                handleClick={(index: number) => {
                  handleParentListItemClick(index, treatmentPlanInfo ?? null);
                  setAnchorEl(parentCardRef?.current ?? null);
                }}
              />
              <Box ref={parentCardRef}></Box>

              <ClientInsightCard
                loading={clientLoading}
                sx={{ mt: 2, ...cardStyles }}
                title="BT"
                labels={getBTLabels(client)}
                showPoperIcon={!openPoper}
                handleClick={(index: number) => {
                  handleBTListItemClick(index, client ?? null);
                  setAnchorEl(btCardRef?.current ?? null);
                }}
              />
              <Box ref={btCardRef}></Box>

              <ClientInsightCard
                title="Home BCBA"
                sx={{ mt: 2, ...cardStyles }}
                loading={providerAssignmentsLoading}
                labels={getBCBAProvierLabels(providerAssignments, ClinicalRelationships.HomeBcba)}
                showPoperIcon={!openPoper}
                handleClick={(index: number) => {
                  handleBCBAListItemClick(
                    index,
                    providerAssignments,
                    ClinicalRelationships.HomeBcba,
                  );
                  setAnchorEl(homeBCBARef?.current ?? null);
                }}
              />
              <Box ref={homeBCBARef}></Box>

              <ClientInsightCard
                title="Substitute BCBA"
                sx={{ mt: 2, ...cardStyles }}
                loading={providerAssignmentsLoading}
                labels={getBCBAProvierLabels(
                  providerAssignments,
                  ClinicalRelationships.SubstituteBCBA,
                )}
                showPoperIcon={!openPoper}
                handleClick={(index: number) => {
                  handleBCBAListItemClick(
                    index,
                    providerAssignments,
                    ClinicalRelationships.SubstituteBCBA,
                  );
                  setAnchorEl(substitudeBCBARef?.current ?? null);
                }}
              />
              <Box ref={substitudeBCBARef}></Box>

              <ClientInsightCard
                title="Bobbing BCBA"
                sx={{ mt: 2, ...cardStyles }}
                loading={providerAssignmentsLoading}
                labels={getBCBAProvierLabels(
                  providerAssignments,
                  ClinicalRelationships.BobbingBcba,
                )}
                showPoperIcon={!openPoper}
                handleClick={(index: number) => {
                  handleBCBAListItemClick(
                    index,
                    providerAssignments,
                    ClinicalRelationships.BobbingBcba,
                  );
                  setAnchorEl(bobingBCBARef?.current ?? null);
                }}
              />
              <Box ref={bobingBCBARef}></Box>
            </Grid>
          </Grid>
          <FHPopper
            anchorEl={anchorEl}
            open={openPoper}
            onClose={handleClose}
            backgroundColor={FORTA_GRAY1}
            popperStyles={{
              zIndex: 100000,
            }}
            sx={{
              margin: 1,
              border: `2px solid ${FORTA_BLUE.light3}`,
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                minWidth: 300,
                p: 0.5,
              }}
            >
              <Typography color={FORTA_BLUE.primary} fontSize={21} mb={2}>
                {poperContent?.type}
              </Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography fontWeight={700} mb={1} color={FORTA_BLUE.primary}>
                    {`${poperContent?.name} (ID: ${poperContent.id})`}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography fontWeight={700}>Phone</Typography>

                    {poperContent.phone ? (
                      <FHCopyToClipboard
                        text={phoneNumberFormatter(poperContent.phone?.toString())}
                      />
                    ) : (
                      'N/A'
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography fontWeight={700}>Email</Typography>

                    {poperContent.email ? (
                      <FHCopyToClipboard text={poperContent.email.toString()} />
                    ) : (
                      'N/A'
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </FHPopper>
        </Box>
      ) : (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', mt: 2 }}
        >
          <ErrorAlertIcon width={18} height={18} viewBox="0 0 26 24" />
          <Typography fontSize={16} textAlign="center" my={2} color={FORTA_BLUE.primary}>
            Please select a client to view details
          </Typography>
        </Box>
      )}
    </>
  );
}

export default ClientInsightDetails;
